/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The Portais introduction while the agency has no portal yet, drawn like the
 * Facebook & Instagram one: what it gets, what it needs and one button per way
 * of connecting (Canal Pro for ZAP, Viva Real and OLX, OLX alone, and
 * Chaves na Mão when its flag is on).
 */

import { Anchor, Text, Title, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { CanalProBadge } from './CanalProBlock';
import { CNM_RED, ChavesNaMaoBadge } from './ChavesNaMaoBlock';
import { IntroLists, OLX_PLANS_URL, OLX_PURPLE, OlxBadge } from './OlxConnectionBlock';
import type { PortalSettings } from './constants';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const CANAL_PRO_GRADIENT = 'linear-gradient(135deg, #6E0AD6, #F28000)';

/** Whether the agency has no portal on: no OLX account, no Canal Pro feed, no Chaves na Mão. */
export function noPortalConnected(settings: PortalSettings, canalProOn: boolean, chavesNaMaoOn = false): boolean {
  return !settings.olx_connected && !canalProOn && !chavesNaMaoOn;
}

export interface PortalsConnectIntroProps {
  saving: boolean;
  /** Turns the Canal Pro feed on (ZAP, Viva Real and OLX). */
  onConnectCanalPro: () => void;
  /** Starts the OLX sign-in (OLX only, through its API). */
  onConnectOlx: () => void;
  /** Turns Chaves na Mão on; the option is shown only when given. */
  onConnectChavesNaMao?: () => void;
  t: Translate;
}

/**
 * One card with every way to reach the portals.
 *
 * @param props - Saving state, the two connect actions and translations.
 * @returns The card body.
 */
export function PortalsConnectIntro({ saving, onConnectCanalPro, onConnectOlx, onConnectChavesNaMao, t }: PortalsConnectIntroProps) {
  const withCnm = !!onConnectChavesNaMao;
  return (
    <div data-testid="portals-connect-intro">
      <div className="flex justify-center mb-(--space-4)">
        <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm">
          <CanalProBadge size={36} />
        </div>
        <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm -ml-(--space-2)">
          <OlxBadge size={36} />
        </div>
        {withCnm && (
          <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm -ml-(--space-2)">
            <ChavesNaMaoBadge size={36} />
          </div>
        )}
      </div>

      <Title order={2} size="lg" className="m-0 text-center font-semibold text-(--text-primary)">{t('portaisIntro.titulo')}</Title>
      <Text className="m-0 mt-(--space-1) text-center text-sm text-(--text-secondary) leading-relaxed">{t(withCnm ? 'portaisIntro.descricaoComChaves' : 'portaisIntro.descricao')}</Text>

      <IntroLists
        benefitsTitle={t('olx.beneficiosTitulo')}
        benefits={[
          t('portaisIntro.beneficio1'), t('portaisIntro.beneficio2'), t('portaisIntro.beneficio3'),
          ...(withCnm ? [t('portaisIntro.beneficioChaves')] : []),
        ]}
        requirementsTitle={t('olx.prerequisitosTitulo')}
        requirements={[
          t('portaisIntro.prerequisito1'), t('portaisIntro.prerequisito2'),
          ...(withCnm ? [t('portaisIntro.prerequisitoChaves')] : []),
        ]}
      />

      <div className="flex flex-col gap-(--space-3)">
        <ConnectButton
          testId="portals-connect-canal-pro"
          background={CANAL_PRO_GRADIENT}
          disabled={saving}
          onClick={onConnectCanalPro}
          icon={<CanalProBadge size={18} />}
          label={t('portaisIntro.conectarCanalPro')}
        />
        <ConnectButton
          testId="portals-connect-olx"
          background={OLX_PURPLE}
          onClick={onConnectOlx}
          icon={<OlxBadge size={18} onBrand={false} />}
          label={t('portaisIntro.conectarOlx')}
        />
        {onConnectChavesNaMao && (
          <ConnectButton
            testId="portals-connect-chaves-na-mao"
            background={CNM_RED}
            disabled={saving}
            onClick={onConnectChavesNaMao}
            icon={<ChavesNaMaoBadge size={18} />}
            label={t('portaisIntro.conectarChaves')}
          />
        )}
      </div>

      <Text className="m-0 text-center text-xs text-(--text-tertiary) mt-(--space-3)">{t('portaisIntro.diferenca')}</Text>
      <Text className="m-0 text-center text-xs text-(--text-secondary) mt-(--space-4)">
        {t('olx.ajudaPlano')}{' '}
        <Anchor href={OLX_PLANS_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--seed-blue)] hover:underline">
          {t('olx.ajudaPlanoLink')}
        </Anchor>
      </Text>
    </div>
  );
}

/** A brand-coloured connect button with an icon and a label. */
function ConnectButton({ testId, background, disabled, onClick, icon, label }: {
  testId: string; background: string; disabled?: boolean; onClick: () => void; icon: React.ReactNode; label: string;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md) text-white',
        'flex items-center justify-center gap-(--space-2)',
        'cursor-pointer border-none transition-opacity hover:opacity-90 disabled:opacity-60',
      )}
      style={{ background }}
    >
      {icon}
      {label}
    </UnstyledButton>
  );
}
